import Tooltip from '@mortgage-pos/ui/components/Tooltip'
import React, { useEffect, useState } from 'react'
import fundsIcon from '@mortgage-pos/ui/assets/funds-icon.svg'
import helpfulIcon from '@mortgage-pos/ui/assets/helpful-icon.svg'
import {
  customTextMap,
  iconMap,
  loaderMap,
} from '@mortgage-pos/ui/utils/customizationHelpers'

import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { useStoreState } from '@mortgage-pos/ui/store'
import { LoanPurpose } from '@mortgage-pos/types'
import { environment } from '@mortgage-pos/ui/env'

interface ValuePropData {
  icon: IconValueProp
  heading: string
  copy?: string
  tooltipCopy?: string
}

interface IconValueProp {
  name: string
  alt: string
}

const smRefinanceValuePropData: ValuePropData[] = [
  {
    icon: {
      name: fundsIcon,
      alt: 'Funds icon',
    },
    heading: 'Get funds at record speed',
    copy: "We're 30% faster than industry average so you can get back to living your life.",
    tooltipCopy:
      "Based on Sage's average days to close conventional refinance loans compared to the industry average in Feb 2022 per ICE mortgage technology origination insights report.",
  },
  {
    icon: {
      name: iconMap('equityIcon'),
      alt: 'Equity icon',
    },
    heading: 'Tap into your home equity',
    copy: "We've helped our customers access $200M in home equity!",
    tooltipCopy: `Since May 2021, ${customTextMap(
      'sageFullPartnerName'
    )} has helped borrowers access over $200M in equity from their home through cash out refinancing.`,
  },
]

const shlRefinanceValuePropData: ValuePropData[] = [
  {
    icon: {
      name: fundsIcon,
      alt: 'Funds icon',
    },
    heading: 'A dedicated Loan Officer',
    copy: 'We have helped over 18K Borrowers achieve their mortgage goals!',
    tooltipCopy:
      'Based on the Borrowers who closed on a mortgage transaction with Sage since October 2014.',
  },
  {
    icon: {
      name: iconMap('equityIcon'),
      alt: 'Equity icon',
    },
    heading: 'Did we mention...',
    copy: "We've helped our customers finance over $5.5B.",
    tooltipCopy: `Since October 2014, ${customTextMap(
      'sageFullPartnerName'
    )} has closed transactions amounting to over $5.5B.`,
  },
]

const sageRefinanceValuePropData: ValuePropData[] = [
  ...(environment.productName === 'sage'
    ? smRefinanceValuePropData
    : shlRefinanceValuePropData),
  {
    icon: {
      name: helpfulIcon,
      alt: 'Helpful icon',
    },
    heading: 'Helpful service',
    copy: "We're here to help you when you need us - and will leave you alone when you don't.",
  },
]

const sagePurchaseValuePropData: ValuePropData[] = [
  {
    icon: {
      name: fundsIcon,
      alt: 'Funds icon',
    },
    heading: 'Make offers confidently',
    copy: 'A Sage pre-qualification will help you figure out what you can afford and confidently make offers to buy.',
  },
  {
    icon: {
      name: iconMap('equityIcon'),
      alt: 'Equity icon',
    },
    heading: 'Custom rates and costs',
    copy: 'Your dedicated Loan Officer will work with you to find the best fit for your unique scenario.',
  },
  {
    icon: {
      name: helpfulIcon,
      alt: 'Helpful icon',
    },
    heading: 'Helpful service',
    copy: "We're here to help you when you need us - and will leave you alone when you don't.",
  },
]

const bankrateValuePropsData: ValuePropData[] = [
  {
    heading: 'Custom rates',
    icon: { name: iconMap('valuePropMoney'), alt: 'Rates icon' },
    copy: 'These are real offers based on your unique financial situation.',
  },
  {
    heading: 'Precise info',
    icon: { name: iconMap('valuePropInfo'), alt: 'Precise info icon' },
    copy: 'We survey national lenders daily to bring you the most accurate rates available.',
  },
  {
    heading: 'Lower offers',
    icon: { name: iconMap('valuePropLowRates'), alt: 'Low Rate icon' },
    copy: "On Bankrate, you'll find rates that are consistently lower than the national average.",
  },
]

const LoadingWithValueProps = () => {
  const { BANKRATE_COMPARE_ENABLED } = FEATURE_FLAG_TYPES
  const isBankrateCompare = useVariation(BANKRATE_COMPARE_ENABLED)
  const { mergedAnswers } = useStoreState((state) => state.answers)

  const isRefi = mergedAnswers?.loanPurpose === LoanPurpose.Refinance

  const valuePropData = isBankrateCompare
    ? bankrateValuePropsData
    : isRefi
    ? sageRefinanceValuePropData
    : sagePurchaseValuePropData

  const carouselItemsLength = valuePropData.length

  const [isActive, setActive] = useState(0)
  const [isNext, setNext] = useState(1)
  const [isPrev, setPrev] = useState(2)
  const [currentSlide, setCurrentSlide] = useState(1)
  const [shouldCarouselCycle, setShouldCarouselCycle] = useState(true)

  const moveNext = () => {
    if (shouldCarouselCycle) {
      if (currentSlide === carouselItemsLength - 1) setCurrentSlide(0)
      else setCurrentSlide(currentSlide + 1)

      moveCarouselTo(currentSlide)
    }
  }

  const moveCarouselTo = (slide: number) => {
    let newPrevious = slide - 1
    let newNext = slide + 1

    if (carouselItemsLength - 1 >= 2) {
      if (slide === 0) {
        newPrevious = carouselItemsLength - 1
      } else if (slide === carouselItemsLength - 1) {
        newPrevious = slide - 1
        newNext = 0
      }

      setNext(newNext)
      setActive(slide)
      setPrev(newPrevious)
    }
  }

  useEffect(() => {
    const valuePropInterval = setInterval(moveNext, 3500)
    return () => clearInterval(valuePropInterval)
  })

  const handleCarouselItemLeave = () => {
    setShouldCarouselCycle(true)
  }

  const handleCarouselItemEnter = () => {
    setShouldCarouselCycle(false)
  }

  return (
    <div className="loading-val-props" data-testid="loading">
      <img
        className="loading-val-props__gif"
        src={loaderMap('checklist')}
        alt="Loading indicator"
      />

      <div className="loading-val-props__content">
        <div className="loading-val-props__top">
          <h2 className="loading-val-props__heading">
            We're putting together your custom rates!
          </h2>

          <p className="loading-val-props__text">
            Our tech is hard at work doing all that nerdy math stuff to help you
            get the best rates. This could take up to 15 seconds. (It'll be
            worth the wait, promise.)
          </p>
        </div>

        <div className="loading-val-props__carousel-wrapper">
          <div className="loading-val-props__carousel">
            {valuePropData.map((valueProp, i) => {
              return (
                <div
                  onMouseEnter={handleCarouselItemEnter}
                  onMouseLeave={handleCarouselItemLeave}
                  className={`loading-val-props__carousel-prop
                     ${isNext === i ? 'next' : ''}
                     ${isActive === i ? 'active' : ''}
                     ${isPrev === i ? 'prev' : ''}`}
                  key={i}
                >
                  <img
                    className="loading-val-props__carousel-icon"
                    src={valueProp.icon.name}
                    alt={valueProp.icon.alt}
                  />

                  <h3 className="loading-val-props__carousel-heading">
                    {valueProp.heading}
                    {valueProp.tooltipCopy && (
                      <Tooltip
                        className="loading-val-props__tooltip"
                        positioningStrategy="absolute"
                        placement="left"
                        text={valueProp.tooltipCopy}
                      />
                    )}
                  </h3>

                  <small className="loading-val-props__carousel-copy">
                    {valueProp.copy}
                  </small>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoadingWithValueProps
